import React, { useState, useRef, useEffect, useMemo } from "react";
import { Input, Button, Select, Drawer, Card, Tag, Tooltip } from "antd";
import {
  SearchOutlined,
  RightOutlined,
  GiftOutlined,
  CreditCardOutlined,
  AppstoreOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import db from "../../../../database";
import { getPricingRules } from "../../../../utils/pricingRulesCache";

const TAG_COLORS = [
  "#3b82f6", // blue
  "#22c55e", // green
  "#f97316", // orange
  "#8b5cf6", // purple
  "#ef4444", // red
  "#06b6d4", // cyan
  "#ec4899", // pink
];

const TAG_ICONS = {
  ITEM: <GiftOutlined />,
  BILL: <ShoppingCartOutlined />,
  BXGY: <TagsOutlined />,
  COMBO: <AppstoreOutlined />,
  DEFAULT: <TagsOutlined />,
  BXPY: <CreditCardOutlined />,
};

const TYPE_TO_TAG = {
  PD: "ITEM",
  FD: "BILL",
  QD: "ITEM",
  TD: "BILL",
  TDF: "BILL",
  EXD: "BILL",
  PA: "ITEM",
  XGY: "BXGY",
  BXD: "BXPY",
  BXS: "BXPY",
  PK: "COMBO",
};

const APPLIES_TO_BY_TYPE = {
  TD: "Entire Bill",
  PD: "Entire Bill",
  TDF: "Entire Bill",
  FD: "Entire Bill",
  PK: "Entire Bill",
  XGY: "Selected Items",
  BXS: "Selected Items",
  BXD: "Selected Items",
  EXD: "Selected Items",
  PA: "Selected Items",
  QD: "Selected Items",
};

const getColorById = (id) => {
  if (!id) return TAG_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

const resolveTagMeta = (rule) => {
  const type = rule?.type;
  return {
    label: TYPE_TO_TAG[type] || type || "DEFAULT",
    group: TYPE_TO_TAG[type] || "DEFAULT",
    icon: TAG_ICONS[TYPE_TO_TAG[type]] || TAG_ICONS.DEFAULT,
    color: getColorById(rule?.mPricingrulesId),
  };
};

const buildCategoriesFromDB = (pricingRules) => {
  const categories = {};
  pricingRules.forEach((rule) => {
    const type = rule.type || "UNKNOWN";
    if (!categories[type]) {
      categories[type] = {
        key: type,
        label: rule.printedName || rule.name || type,
        count: 0,
        offers: [],
      };
    }
    categories[type].count += 1;
    categories[type].offers.push(rule);
  });
  return {
    all: {
      key: "all",
      label: "ALL",
      count: pricingRules.length,
      offers: pricingRules,
    },
    ...categories,
  };
};

const OfferCard = ({ ruleId, title, description, ruleType, valid, tagMeta, onViewDetails }) => {
  const [isClamped, setIsClamped] = useState(false);
  const titleRef = useRef(null);
  const appliesTo = APPLIES_TO_BY_TYPE[ruleType] || "Entire Bill";

  useEffect(() => {
    if (titleRef.current) {
      setIsClamped(titleRef.current.scrollHeight > titleRef.current.clientHeight);
    }
  }, [title]);

  return (
    <Card
      hoverable
      style={{
        width: 384,
        height: 240,
        borderRadius: 16,
        border: "1px solid #e5e7eb",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s",
      }}
      styles={{ body: {
        padding: 20,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "space-between",
      } }}
    >
      {/* TITLE + TAG */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: description ? 10 : 18,
        }}
      >
        <div
          title={title}
          style={{
            fontSize: 18,
            fontWeight: 500,
            color: "#111827",
            lineHeight: "24px",
            flex: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "pointer",
          }}
        >
          {title}
        </div>

        <Tag
          style={{
            background: tagMeta.color,
            color: "#fff",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 2,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          {tagMeta.icon}
          <span>{tagMeta.label}</span>
        </Tag>
      </div>

      {/* DESCRIPTION */}
      {description && (
        <div
          title={description}
          style={{
            marginTop: 4,
            marginBottom: 8,
            fontSize: 13,
            color: "#9ca3af",
            lineHeight: "20px",
            maxHeight: "40px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "pointer",
          }}
        >
          {description}
        </div>
      )}

      {/* META */}
      <div>
        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
          <span style={{ fontWeight: 500, color: "#111827" }}>Applies To:</span> <span style={{ fontWeight: 400 }}>{appliesTo}</span>
        </div>
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          <span style={{ fontWeight: 500, color: "#111827" }}>Valid:</span> <span style={{ fontWeight: 500, color: "#10b981" }}>{valid}</span>
        </div>
      </div>

      {/* FOOTER */}
      <div
        onClick={onViewDetails}
        style={{
          marginTop: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          padding: "7px 7px",
          borderRadius: 10,
          color: "black",
          transition: "all 0.15s ease",
        }}
        className="offer-view-details"
      >
        <span>View Details</span>
        <RightOutlined style={{ fontSize: 14 }} />
      </div>
    </Card>
  );
};

const OffersDrawer = ({
  offersDrawerOpen,
  setOffersDrawerOpen,
  handleViewDetails,
  formatDate,
}) => {
  const [categories, setCategories] = useState({});
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchText, setSearchText] = useState("");

  const handleCloseDrawer = () => {
    setOffersDrawerOpen(false);
    setSearchText("");
    setActiveCategory("all");
  };

  useEffect(() => {
    if (offersDrawerOpen) {
      setSearchText("");
      setActiveCategory("all");
    }
  }, [offersDrawerOpen]);

  useEffect(() => {
    const pricingRules = getPricingRules();
    const categorized = buildCategoriesFromDB(pricingRules);
    setCategories(categorized);
  }, []);

  const offers = useMemo(() => {
    const categoryOffers = activeCategory === "all" ? categories.all?.offers || [] : categories[activeCategory]?.offers || [];
    if (!searchText.trim()) {
      return categoryOffers;
    }
    const search = searchText.toLowerCase();
    return categoryOffers.filter((rule) => {
      const title = (rule.printedName || rule.name || "").toLowerCase();
      return title.includes(search);
    });
  }, [categories, activeCategory, searchText]);

  return (
    <Drawer
      placement="bottom"
      height="93vh"
      open={offersDrawerOpen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      onClose={handleCloseDrawer}
      styles={{ body: {
        padding: "16px 24px",
        background: "#fafafa",
      } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 16,
        }}
      >
        {/* LEFT TITLE */}
<h2
  style={{
    margin: 0,
    fontWeight: 600,
    fontSize: "1.5vw",
  }}
>
  Offers & Promotions
</h2>

        {/* RIGHT CONTROLS */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* SEARCH INPUT */}
          <Input
            placeholder="Search offers by name"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{
              width: 320,
              borderRadius: 6,
            }}
          />

          {/* SORT DROPDOWN */}
          <Select
            defaultValue="latest"
            style={{ width: 120 }}
            options={[
              { value: "latest", label: "Latest" },
              { value: "oldest", label: "Oldest" },
              { value: "expiry", label: "Expiry Date" },
            ]}
          />

          {/* CLOSE BUTTON */}
          <Button type="text" onClick={handleCloseDrawer}>
            ✕
          </Button>
        </div>
      </div>

      {/* CATEGORY SECTION */}
      <div style={{ margin: "0 -24px" }}>
        {/* TOP DIVIDER */}
        <div style={{ height: 1, background: "#e6e6e6" }} />

        {/* CATEGORIES */}
        <div
          style={{
            padding: "12px 24px",
            display: "flex",
            gap: 8,
            flexWrap: "nowrap",
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            scrollbarWidth: "thin",
          }}
          className="category-scroll"
        >
          {Object.values(categories).map((cat) => {
            const isActive = activeCategory === cat.key;

            return (
              <Button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                style={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  height: 28,
                  padding: "0 12px",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  background: isActive ? "#334155" : "#ffffff",
                  color: isActive ? "#ffffff" : "#111827",
                  border: isActive ? "1px solid #334155" : "1px solid #e5e7eb",
                }}
              >
                <span>{cat.label}</span>

                <span
                  style={{
                    background: isActive ? "#ffffff" : "#f3f4f6",
                    color: isActive ? "#334155" : "#374151",
                    borderRadius: 6,
                    padding: "0 6px",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {cat.count}
                </span>
              </Button>
            );
          })}
        </div>

        {/* BOTTOM DIVIDER */}
        <div style={{ height: 1, background: "#e6e6e6" }} />
      </div>

      {/* OFFERS (SCROLLABLE) */}
      <div
        style={{
          marginTop: 16,
          height: "calc(92vh - 140px)",
          overflowY: "auto",
          paddingRight: 6,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          {offers.map((rule) => {
            const tagMeta = resolveTagMeta(rule);

            return (
              <OfferCard
                ruleType={rule.type}
                key={rule.mPricingrulesId}
                ruleId={rule.mPricingrulesId}
                title={rule.printedName || rule.name}
                description={rule.description || ""}
                tagMeta={tagMeta}
                appliesTo="Entire Bill"
                valid={`${formatDate(rule.startDate)} – ${formatDate(rule.endDate)}`}
                onViewDetails={() => handleViewDetails(rule)}
              />
            );
          })}
        </div>
      </div>
    </Drawer>
  );
};

export default OffersDrawer;
